import React, { useState, useRef, useEffect, ChangeEvent } from 'react';
import { Send, Paperclip, X, FileText, ImageIcon, Loader2, Plus, Settings, Database, Mic, MicOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAudioRecorder } from '../../../hooks/useAudioRecorder';
import RecordingWaveform from './RecordingWaveform';
import ChatService from '../../../services/ChatService';

export interface UploadedFile {
  id: string;
  name: string;
  type: string;
  size: number;
  preview?: string;
  base64?: string;
  gcsUri?: string;
  isUploading?: boolean;
}

export interface PromptInputProps {
  onSubmit: (prompt: string, files: UploadedFile[]) => void;
  placeholder?: string;
  className?: string;
  isLoading?: boolean;
  disabled?: boolean;
  availableCatalogs?: string[];
  /** Prefill the box (e.g. a "Start with" pill on the home page). Applied whenever it changes. */
  initialValue?: string;
}

const PromptInput: React.FC<PromptInputProps> = ({
  onSubmit,
  placeholder,
  className = '',
  isLoading = false,
  disabled = false,
  initialValue,
  availableCatalogs = [],
}) => {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState('');
  useEffect(() => { if (initialValue !== undefined) setPrompt(initialValue); }, [initialValue]);
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [showCatalogDropdown, setShowCatalogDropdown] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const catalogDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!disabled) {
      textareaRef.current?.focus();
    }
  }, [disabled]);

  const { status: audioStatus, isSupported: audioSupported, toggle: toggleAudio, stop: stopAudio } = useAudioRecorder({
    onAudioFile: (base64, mimeType) => {
      const ext = mimeType.includes('ogg') ? 'ogg' : mimeType.includes('mp4') ? 'mp4' : 'webm';
      const audioFile: UploadedFile = {
        id: `audio_${Date.now()}`,
        name: `voice-message.${ext}`,
        type: mimeType,
        size: Math.round((base64.length * 3) / 4),
        base64: `data:${mimeType};base64,${base64}`,
      };
      setFiles((prev) => {
        const updated = [...prev, audioFile];
        // Auto-submit on next tick so state is updated
        setTimeout(() => {
          onSubmit(prompt.trim(), updated);
          setPrompt('');
          setFiles([]);
        }, 0);
        return updated;
      });
    },
  });
  const isRecording = audioStatus === 'recording';
  const isProcessingAudio = audioStatus === 'processing';

  const handleFileSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles) return;

    const newFiles: UploadedFile[] = [];

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      const reader = new FileReader();

      await new Promise<void>((resolve) => {
        reader.onload = () => {
          const base64 = reader.result as string;
          const uploadedFile: UploadedFile = {
            id: `${Date.now()}-${i}`,
            name: file.name,
            type: file.type,
            size: file.size,
            base64: base64,
            isUploading: true,
          };

          // Create preview for images
          if (file.type.startsWith('image/')) {
            uploadedFile.preview = base64;
          }

          newFiles.push(uploadedFile);
          resolve();
        };

        reader.readAsDataURL(file);
      });
    }

    setFiles((prev) => [...prev, ...newFiles]);

    // Kick off GCS pre-upload in the background for each file
    newFiles.forEach((uploadedFile) => {
      const base64 = uploadedFile.base64?.split(',')[1] || '';
      if (!base64) return;
      ChatService.uploadChatFile(base64, uploadedFile.type, uploadedFile.name, 'pending')
        .then((result) => {
          setFiles((prev) =>
            prev.map((f) =>
              f.id === uploadedFile.id ? { ...f, gcsUri: result.gcsUri, isUploading: false } : f
            )
          );
        })
        .catch((err) => {
          console.warn('PromptInput: pre-upload failed, will fall back to base64:', err);
          setFiles((prev) =>
            prev.map((f) =>
              f.id === uploadedFile.id ? { ...f, isUploading: false } : f
            )
          );
        });
    });

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const pastedImages = Array.from(event.clipboardData.files).filter((file) =>
      file.type.startsWith('image/')
    );
    if (pastedImages.length === 0 || !fileInputRef.current) return;

    // Reuse the normal attachment flow so pasted images get the same preview,
    // pre-upload, loading state, and submit behavior as picked files.
    event.preventDefault();
    const transfer = new DataTransfer();
    pastedImages.forEach((file) => transfer.items.add(file));
    fileInputRef.current.files = transfer.files;
    fileInputRef.current.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const handleRemoveFile = (fileId: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  const isAnyFileUploading = files.some((f) => f.isUploading);

  const handleSubmit = () => {
    if ((prompt.trim() || files.length > 0) && !isLoading && !disabled && !isAnyFileUploading) {
      if (isRecording) stopAudio();
      onSubmit(prompt.trim(), files);
      setPrompt('');
      setFiles([]);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (isRecording) stopAudio();
      handleSubmit();
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' ' + t('promptInput.fileSize.bytes', { defaultValue: 'B' });
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' ' + t('promptInput.fileSize.kilobytes', { defaultValue: 'KB' });
    return (bytes / (1024 * 1024)).toFixed(1) + ' ' + t('promptInput.fileSize.megabytes', { defaultValue: 'MB' });
  };

  // Auto-resize textarea
  React.useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [prompt]);

  // Close catalog dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (catalogDropdownRef.current && !catalogDropdownRef.current.contains(event.target as Node)) {
        setShowCatalogDropdown(false);
      }
    };

    if (showCatalogDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showCatalogDropdown]);

  return (
    <div className={`w-full ${className}`}>
      {/* File Previews - Outside the main input */}
      {files.length > 0 && (
        <div className="mb-3 px-1">
          <div className="flex flex-wrap gap-2">
            {files.map((file) => (
              <div
                key={file.id}
                className="group relative bg-white rounded-xl border border-gray-200/60 overflow-hidden shadow-sm hover:shadow-md transition-all"
              >
                {file.preview ? (
                  <div className="relative w-16 h-16 sm:w-20 sm:h-20">
                    <img
                      src={file.preview}
                      alt={file.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all" />
                    {file.isUploading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-xl">
                        <svg className="animate-spin w-6 h-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                        </svg>
                      </div>
                    )}
                    <button
                      onClick={() => handleRemoveFile(file.id)}
                      className="absolute -top-1.5 -right-1.5 bg-gray-900 text-white rounded-full p-1 shadow-lg opacity-0 group-hover:opacity-100 transition-all scale-90 group-hover:scale-100"
                      disabled={isLoading}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 p-2.5 pr-3">
                    <div className="bg-gray-100 rounded-lg p-2 relative">
                      <FileText className="w-4 h-4 text-gray-600 flex-shrink-0" />
                      {file.isUploading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white/70 rounded-lg">
                          <svg className="animate-spin w-4 h-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                          </svg>
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-gray-700 truncate max-w-[100px] sm:max-w-[140px]">
                        {file.name}
                      </p>
                      <p className="text-xs text-gray-400">
                        {formatFileSize(file.size)}
                      </p>
                    </div>
                    <button
                      onClick={() => handleRemoveFile(file.id)}
                      className="text-gray-300 hover:text-gray-600 transition-colors ml-1"
                      disabled={isLoading}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Main Input Container */}
      <div className="relative group">
        <div className="bg-white rounded-3xl shadow-[0_2px_12px_rgba(0,0,0,0.08)] border border-gray-200/60 transition-all duration-300 group-hover:shadow-[0_4px_20px_rgba(0,0,0,0.12)] group-focus-within:shadow-[0_4px_20px_rgba(0,0,0,0.12)] group-focus-within:border-gray-300/80">
          {/* Input Area */}
          <div className="p-3 sm:p-4">
            {/* Text Input with Send Button */}
            <div className="flex items-end gap-2">
              <div className="flex-1 flex items-center py-1">
                {isRecording ? (
                  <RecordingWaveform onStop={stopAudio} className="py-0.5" />
                ) : (
                  <textarea
                    ref={textareaRef}
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                    placeholder={
                      isProcessingAudio
                        ? t('chat.audio.processing', { defaultValue: 'Processing audio…' })
                        : (placeholder || t('promptInput.placeholder'))
                    }
                    className="w-full resize-none bg-transparent text-gray-900 placeholder-gray-400/70 focus:outline-none min-h-[28px] max-h-[200px] text-[15px] sm:text-base leading-relaxed"
                    rows={1}
                    disabled={isLoading || disabled}
                    style={{ scrollbarWidth: 'thin' }}
                  />
                )}
              </div>

              {/* Send Button - Right Side — hidden while recording (waveform has its own stop) */}
              {!isRecording && (
                <div className="flex items-end pb-0.5">
                  <button
                    onClick={handleSubmit}
                    disabled={(!prompt.trim() && files.length === 0) || isLoading || disabled || isAnyFileUploading}
                    className="p-2 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-all disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-gray-900 shadow-sm"
                    title={isAnyFileUploading ? t('promptInput.uploading', { defaultValue: 'Uploading files…' }) : t('promptInput.send')}
                  >
                    {isLoading || isAnyFileUploading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                  </button>
                </div>
              )}
            </div>

            {/* Bottom Action Buttons — hidden while recording */}
            {!isRecording && (
            <div className="flex items-center gap-1 mt-2 pt-2 border-t border-gray-100 overflow-visible">
              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,.pdf,.doc,.docx,.txt"
                onChange={handleFileSelect}
                className="hidden"
                disabled={isLoading || disabled}
              />
              
              {/* Add File Button (Plus icon) */}
              <button
                onClick={() => fileInputRef.current?.click()}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100/80 rounded-xl transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                disabled={isLoading || disabled}
                title={t('promptInput.attachFile')}
              >
                <Plus className="w-5 h-5" />
              </button>

              {/* Mic / Voice Input Button */}
              {audioSupported && (
                <button
                  onClick={toggleAudio}
                  disabled={isLoading || disabled || isProcessingAudio}
                  className={`p-2 rounded-xl transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                    isRecording
                      ? 'text-red-500 bg-red-50 hover:bg-red-100 animate-pulse'
                      : isProcessingAudio
                      ? 'text-blue-500 bg-blue-50'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100/80'
                  }`}
                  title={isRecording
                    ? t('chat.audio.stopRecording', { defaultValue: 'Stop recording' })
                    : t('chat.audio.startRecording', { defaultValue: 'Record audio message' })
                  }
                >
                  {isRecording ? (
                    <MicOff className="w-5 h-5" />
                  ) : (
                    <Mic className="w-5 h-5" />
                  )}
                </button>
              )}

              {/* Tools Button (placeholder for future features) */}
              <button
                className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100/80 rounded-xl transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                disabled
                title={t('promptInput.tools', { defaultValue: 'Tools' })}
              >
                <Settings className="w-5 h-5" />
              </button>

              {/* Database Catalogs Button */}
              {availableCatalogs.length > 0 && (
                <div className="relative" ref={catalogDropdownRef}>
                  <button
                    onClick={() => setShowCatalogDropdown(!showCatalogDropdown)}
                    className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-xl transition-all relative"
                    title={t('promptInput.viewCatalogs', { defaultValue: 'View Connected Catalogs' })}
                  >
                    <Database className="w-5 h-5" />
                    {/* Indicator badge */}
                    <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-green-500 rounded-full border-2 border-white"></span>
                  </button>

                  {/* Catalog Dropdown */}
                  {showCatalogDropdown && (
                    <div className="absolute top-full left-0 mt-2 max-h-[60vh] overflow-y-auto bg-white rounded-xl shadow-lg border border-gray-200 py-2 min-w-[200px] z-[100]">
                      <div className="px-3 py-2 border-b border-gray-100">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                          {t('promptInput.connectedCatalogs', { defaultValue: 'Connected Catalogs' })}
                        </p>
                      </div>
                      <div className="max-h-64 overflow-y-auto py-1">
                        {availableCatalogs.map((catalog, index) => (
                          <div
                            key={index}
                            className="px-3 py-2 flex items-center gap-2 hover:bg-gray-50 transition-colors"
                          >
                            <div className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0"></div>
                            <span className="text-sm text-gray-700 font-medium">{catalog}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            )}
          </div>
        </div>
      </div>

      {/* Helper Text - Subtle */}
      <div className="mt-2.5 px-3 text-[11px] sm:text-xs text-gray-400/80 text-center font-light">
        {t('promptInput.helperText')}
      </div>
    </div>
  );
};

export default PromptInput;
